import { IPlatform } from 'types-generator/shared/interfaces'
import { getMode } from '../bridge_interface/bridge_interface'
import adEventCollector, {
  EVENT_AD_LOAD, EVENT_AD_METRIC,
  EVENT_IMPRESSION,
  EVENT_IMPRESSION_VIEWABLE
} from '../utilities/ad-event-collector'
import { KARGO_DIV_ID_PRODUCTION, KARGO_DIV_ID_TEST } from './constants'
import { neptuneDebug } from '../../../utilities'
import loadJS from 'load-js'
import { IPlatformSettingHeaderBidding } from 'types-generator/platform-definition/IPlatformConfiguration'
import { IPageSettingAdCampaigns, TPageSettingLanguage } from 'types-generator/page-configuration/IPageConfiguration'
import * as bridgeConstants from '../bridge_interface/constants'
import { AD_POSITION_FLOATING } from '../../../../process/configureAdBlocks'
import { LS_BRIDGE_UUID, LS_BRIDGE_UUID_INTERNAL } from '../bridge_interface/constants'

const DEFAULT_LANGUAGE = 'en'
export const DEFAULT_DIMENSIONS = [
  [300, 250],
  [320, 100],
  [320, 50]
]
export const DEFAULT_DIMENSIONS_STICKY = [
  [300, 50],
  [320, 50]
]

export const DEFAULT_DIMENSIONS_FLOATING = [
  [0, 0],
  [320, 50]
]

const translatePartnerToAppDetails = (partner: string) => {
  const playStoreIdByPlatform = {
    metropcs: localStorage.getItem('int_mp_appBundle') || 'com.mobileposse.client',
    boost: 'com.mobileposse.client.fastnews',
    cricket: 'com.cricketwireless.thescoop',
    blu: 'com.digitalturbine.android.apps.news.blu',
    pwg: 'com.digitalturbine.android.apps.news.pwg',
    dtom: 'com.digitalturbine.android.apps.news',
    uscc: 'com.digitalturbine.android.apps.news.uscellular',
    moment: 'com.mobileposse.client.moment',
    essentials: 'com.digitalturbine.android.apps.news.att'
  }
  return playStoreIdByPlatform[partner] || 'com.mobileposse.client'
}

export const initDTx = (appId: number, partner: string, brand: string) => {
  if (!appId && !partner && !brand) {
    return
  }
  const w = window as any
  const appBundle = translatePartnerToAppDetails(partner)
  const appVersion =
    localStorage.getItem(bridgeConstants.LS_BRIDGE_APP_VERSION_MP) ??
    localStorage.getItem(bridgeConstants.LS_BRIDGE_APP_VERSION_MP_INTERNAL) ??
    ''
  const aaid = localStorage.getItem(bridgeConstants.LS_BRIDGE_ANDROID_AID)
  let aaidDetails = {}
  if (aaid) {
    aaidDetails = {
      advertiserId: aaid,
      advertiserIdType: 'android'
    }
  }
  let hostingAppDetails = undefined
  if (appBundle && brand) {
    hostingAppDetails = {
      packageName: appBundle,
      appVersion: appVersion,
      appName: brand
    }
  }
  const mpid =
    localStorage.getItem(LS_BRIDGE_UUID) || (new URLSearchParams(w?.location?.search).get('mpid') ?? undefined)
  try {
    const dtx = w.DTXWeb.getInstance()
    dtx.initialize({
      appId: appId,
      hostingAppDetails: hostingAppDetails,
      userId: mpid,
      ...aaidDetails
    })

    dtx.addEventListener('ad_requested', (payload: any) => {
      adEventCollector.emit(EVENT_AD_LOAD, {
        source: 'dtx',
        ...payload
      })
    })

    dtx.addEventListener('impression', (payload: any) => {
      adEventCollector.emit(EVENT_IMPRESSION, {
        source: 'dtx',
        ...payload
      })
    })
    dtx.addEventListener('viewableImpression', (payload: any) => {
      adEventCollector.emit(EVENT_IMPRESSION_VIEWABLE, {
        source: 'dtx',
        ...payload
      })
    })
    dtx.addEventListener('adMetric', (payload: any) => {
      adEventCollector.emit(EVENT_AD_METRIC, {
        source: 'dtx',
        ...payload
      })
    })
  } catch (e) {
    console.log(`Error initializing DTX SDK`, e)
  }
}

export const initPubwise = async (
  platform: IPlatform,
  headerBidding: IPlatformSettingHeaderBidding,
  campaigns: IPageSettingAdCampaigns,
  language: TPageSettingLanguage,
  baseUrl: string
) => {
  try {
    const w = window as any

    w.setup = setupPubwise(platform, headerBidding, campaigns, language)

    w.pbjs.que.push(function () {
      const categoryCodes = window.mp_globals.ad_config.iab_category_codes
      const categoryNames = window.mp_globals.ad_config.categories

      const pathnameMap = window.location.pathname.split('/')
      const filename = pathnameMap[pathnameMap.length - 1]
      const filenameWithoutExtension = filename.split('.html')[0]

      const appVersion =
        localStorage.getItem(bridgeConstants.LS_BRIDGE_APP_VERSION_MP) ??
        localStorage.getItem(bridgeConstants.LS_BRIDGE_APP_VERSION_MP_INTERNAL) ??
        ''

      w.pubwise.extra_dfp_params = {
        ...(w.setup.pubwise.dfp_params ?? {}),
        iab_category_codes: categoryCodes,
        iab_category_names: categoryNames,
        mode: w.getMode(),
        pathname: filenameWithoutExtension,
        mp_app_version: appVersion,
        bundle: localStorage.getItem('int_mp_appBundle')
      }
    })

    if (w.mp_globals.flags.allow_gpt) {
      w.mp_globals.helpers.injectScript(`${baseUrl}/js/pubwise_integration.js`)
      w.mp_globals.helpers.injectScript('//securepubads.g.doubleclick.net/tag/js/gpt.js', {
        async: true
      })
    }

    // Initialize Amazon bidding.
    if (w.mp_globals.features.amazon_ads && w.apstag) {
      w.apstag.init({
        pubID: '0a2e62aa-e1a3-4a86-bb42-43cbd1506fb4',
        adServer: 'googletag'
      })
    }

    if (w.mp_globals.flags.allow_pubwise && w.setup.pubwise.scripts) {
      let index = 1
      const PubwiseLoadedEvent = new Event('PubwiseLoadingSucceed')
      for (const script of w.setup.pubwise.scripts) {
        try {
          await loadJS([script.src])

          if (index === w.setup.pubwise.scripts.length) {
            document.dispatchEvent(PubwiseLoadedEvent)
            ;(window as any).pubwise.loaded = true
          }
          index++
        } catch (e) {
          ;(window as any).pubwise.enabled = false
          ;(window as any).pubwise.loaded = false

          const error = 'error loading pws.js script, setting enabled to false'
          console.error(error)
        }
      }
    }
  } catch (e) {
    console.error('initPubwise failed: %o', e)
  }
}

export const setupPubwise = (platform, pubwiseConfig, campaigns, language) => {
  try {
    return {
      language,
      pubwise: getPubwiseConfig(platform, pubwiseConfig, campaigns, language, navigator.userAgent)
    }
  } catch (e) {
    return {
      language,
      pubwise: {}
    }
  }
}

/**
 * Select pubwise config.
 */
export const getPubwiseConfig = (platform, headerBidding, campaigns, language, userAgent) => {
  const campaign = getCampaignByPlatform(platform, campaigns)

  switch (campaign.type) {
    case 'mode':
      return configureCampaignFsdMode(headerBidding, campaign, language)

    case 'single':
      return configureCampaignSingle(headerBidding, campaign, language)

    case 'split':
      return configureCampaignSplit(headerBidding, campaign, language)

    case 'useragent':
      return configureCampaignUserAgent(headerBidding, campaign, language, userAgent)

    default:
      throw new Error(`campaign type ${campaign.type} not supported`)
  }
}

const getCampaignByPlatform = (platform, settings) => {
  if (settings.custom) {
    for (const custom of settings.custom) {
      if (!custom.platforms) {
        console.error('NEPTUNE ERROR: Custom Pubwise Configuration Is Missing Platforms')
        continue
      }

      if (custom.platforms.includes(platform)) {
        return custom
      }
    }
  }

  return settings.default
}

/**
 * Setup pubwise when differentiating on which mode an FSD is delivered in.
 *
 * See PubwiseCampaignModeFilter in page-definition.ts for option details.
 */
const configureCampaignFsdMode = (headerBidding, campaign, language) => {
  const mode = getMode() || 'default'
  const modeConfigName = campaign?.filter?.[mode] || 'default'

  neptuneDebug(`configureCampaignFsdMode: mode filter ${mode} -> ${modeConfigName}`)

  const config = headerBidding?.[modeConfigName]?.[language] || headerBidding?.[modeConfigName]?.[DEFAULT_LANGUAGE]

  if (!config) {
    throw new Error(`configureCampaignFsdMode failed, config not found for mode ${mode} + ${language}`)
  }

  return headerBidding[modeConfigName][language]
}

/**
 * Setup pubwise when running in single "named" mode.
 *
 * See PubwiseCampaignSingle in page-definition.ts for option details.
 */
const configureCampaignSingle = (headerBidding, campaign, language) => {
  const config = headerBidding?.[campaign.config]?.[language] || headerBidding?.[campaign.config]?.[DEFAULT_LANGUAGE]

  if (!config) {
    throw new Error(`configureCampaignSingle failed, config not found for config ${campaign.config} + ${language}`)
  }

  return config
}

/**
 * Setup pubwise when running in split a/b testing mode.
 *
 * See PubwiseCampaignSplit in page-definition.ts for option details.
 */
const configureCampaignSplit = (headerBidding, campaign, language) => {
  // Check settings.
  if (!campaign.variantPercent) {
    throw new Error('configureCampaignSplit failed, variantPercent is missing')
  } else if (!campaign.control) {
    throw new Error('configureCampaignSplit failed, control is missing')
  } else if (!campaign.variant) {
    throw new Error('configureCampaignSplit failed, variant is missing')
  }

  // Check that configs exist.
  if (!headerBidding?.[campaign.control]?.[language]) {
    throw new Error('configureCampaignSplit failed, control pubwise config missing')
  } else if (!headerBidding?.[campaign.variant]?.[language]) {
    throw new Error('configureCampaignSplit failed, variant pubwise config missing')
  }

  const dice = Math.random() * 100
  const percent = parseInt(campaign.variantPercent, 10)
  const splitConfigName = dice <= percent ? campaign.variant : campaign.control

  neptuneDebug(
    `configureCampaignSplit: split campaign: ${campaign.control} / ${campaign.variant} % ${percent} = ${splitConfigName}`
  )

  const config = headerBidding?.[splitConfigName]?.[language] || headerBidding?.[splitConfigName]?.[DEFAULT_LANGUAGE]

  if (!config) {
    throw new Error(`configureCampaignSplit failed, config not found for config ${splitConfigName} + ${language}`)
  }

  return config
}

const configureCampaignUserAgent = (headerBidding, campaign, language, userAgent) => {
  if (!userAgent) {
    throw new Error(`configureCampaignUserAgent failed, user agent missing`)
  }

  for (const match of campaign.matchList) {
    if (userAgent.toLowerCase().includes(match.toLowerCase())) {
      return headerBidding?.[campaign.config]?.[language] || headerBidding?.[campaign.config]?.[DEFAULT_LANGUAGE]
    }
  }

  return headerBidding?.['default']?.[language] || headerBidding?.['default']?.[DEFAULT_LANGUAGE]
}

export interface AdBlockConfiguration {
  pubwiseConfig: any
  position: any
  adUnitID: any
  loadMethod: 'define_on_unlock' | '' | undefined
  dimensionsOverride: any
  outpageSlot?: {
    customMethod: true
    position: string
  }
  dtxEnabled?: boolean
  dtxPlacementId?: number
  dtxFallbackToGam?: boolean
}

/**
 * The new unified entry point for ad loading.
 * It uses an IntersectionObserver to check for ad block visibility, and when the ad
 * is in view, it executes the provided `loadAd` callback.
 * This replaces the separate visibility checks that were in `loadDTxAdBlock`
 * and the `pwRegisterLazyLoad` function from the Pubwise script.
 */
const waitForViewabilityAndLoad = (config: AdBlockConfiguration, loadAd: (config: AdBlockConfiguration) => void) => {
  const targetDivId = config.adUnitID
  const targetElement = document.getElementById(targetDivId)

  if (!targetElement) {
    console.error(`Ad block target element with ID "${targetDivId}" not found.`)
    return
  }

  const lazyLoadEnabled = isLazyLoadEnabled(config.position, config.adUnitID, config.pubwiseConfig?.lazyload_top)
  if (!lazyLoadEnabled) {
    loadAd(config)
    return
  }

  // Replicate the logic from the now-removed pwRegisterLazyLoad function.
  // These values were previously hardcoded in the call to that function.
  const rootMargin = [50, 0, 50, 0]
  const threshold = 0
  const mobileBreakpoint = 768
  const mobileMultiple = 2

  let respWidth: number
  try {
    respWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
  } catch (error) {
    respWidth = mobileBreakpoint
  }

  // Clone the array to avoid mutation issues.
  const finalRootMargin = [...rootMargin]

  // Adjust the vertical margins for mobile viewports to trigger the ad load sooner.
  for (let i = 0; i < finalRootMargin.length; i++) {
    if ((i === 0 || i === 2) && respWidth < mobileBreakpoint) {
      finalRootMargin[i] = finalRootMargin[i] * mobileMultiple
    }
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      const [entry] = entries
      if (entry.isIntersecting) {
        obs.disconnect() // Fire only once
        neptuneDebug(`Ad block "${targetDivId}" is now viewable. Loading ad.`)
        loadAd(config)
      }
    },
    {
      root: null,
      rootMargin: finalRootMargin.map(m => `${m}px`).join(' '),
      threshold: threshold
    }
  )
  observer.observe(targetElement)
}

const requestDtxAd = (config: AdBlockConfiguration) => {
  const targetDivId = config.adUnitID
  const bannerSizes = getAdBlockDimensions(config.adUnitID, config.dimensionsOverride, config.position, true).map(
    sz => {
      return { width: sz[0], height: sz[1] }
    }
  )
  const w = window as any
  const bannerAd = w.DTXWeb.getInstance().banner(config.dtxPlacementId)

  const requestPromise = (async (): Promise<boolean> => {
    try {
      const adRequest = {
        bannerSizes: bannerSizes,
        deviceLanguage: document.documentElement.getAttribute('lang')
      }
      let testData = undefined
      const searchParams = new URLSearchParams(window.location.search)
      const dtxTestResponseId = searchParams.get('dtx-test-response-id')
      const dtxTestDistributorId = searchParams.get('dtx-test-distributor-id')
      if (dtxTestDistributorId && dtxTestResponseId) {
        testData = {
          mock_response_id: dtxTestResponseId,
          distributor_id: Number(dtxTestDistributorId)
        }
      }
      if (testData) {
        adRequest['testData'] = {
          test_data: testData
        }
      }
      const result = await bannerAd.request(adRequest)
      if (result.success) {
        bannerAd.render(targetDivId)
        return true
      } else {
        console.error(`DTX | Error loading ad`, result.error)
      }
      return false
    } catch (e) {
      console.error(`DTX | Unexpected error during ad request for "${config.adUnitID}"`, e)
      return false
    }
  })()

  return { bannerAd, requestPromise }
}

const _loadGamAds = (config: AdBlockConfiguration) => {
  const { pubwiseConfig, adUnitID, loadMethod } = config

  if (!pubwiseConfig) {
    // This was a typo, "definite" should be "define"
    neptuneDebug(`Could not define ad block for ${adUnitID}, pubwise config is empty`)
    return
  }

  // Define immediately.
  // The lazy loading is now handled by waitForViewabilityAndLoad, so we can remove the
  // check here and just call adBlockConfigureGPT.
  if (loadMethod !== 'define_on_unlock') {
    adBlockConfigureGPT(config)
    return
  }

  // Loading on unlock was created for ad ops as a result of getting marked as invalid traffic
  // with some advertisers. This logic is separate from lazy loading and should be kept.
  ;(window as any).bridgeInterface.queueUnlockTask(() => {
    neptuneDebug(`Queueing ad slot definition call for ${adUnitID} until screen unlock event.`)
    adBlockConfigureGPT(config)
  })
}

const loadGamAds = (config: AdBlockConfiguration) => {
  const { dtxEnabled, dtxPlacementId, dtxFallbackToGam, adUnitID } = config
  if (dtxEnabled && dtxPlacementId) {
    const { bannerAd, requestPromise } = requestDtxAd(config)
    const timeoutPromise = new Promise<'timeout'>(resolve => setTimeout(() => resolve('timeout'), 5_500))

    Promise.race([requestPromise, timeoutPromise])
      .then(result => {
        if (result === 'timeout') {
          if (bannerAd && typeof bannerAd.dispose === 'function') {
            neptuneDebug(`DTX ad request for "${adUnitID}" timed out, disposing banner.`)
            try {
              bannerAd.dispose()
            } catch (e) {
              /* empty */
            }
          }
          console.error(`DTX ad request for "${adUnitID}" timed out, falling back to GAM.`)
          if (dtxFallbackToGam) {
            _loadGamAds(config)
          }
          return
        }

        const success = result as boolean
        if (!success && dtxFallbackToGam) {
          neptuneDebug(`DTX ad load failed for "${adUnitID}", falling back to GAM.`)
          _loadGamAds(config)
        } else if (success) {
          const adElement = document.getElementById(adUnitID)
          if (adElement) {
            adElement.classList.remove('loading')
          }
        }
      })
      .catch(error => {
        console.error(`DTX ad loading for "${adUnitID}" failed unexpectedly, falling back to GAM.`, error)
        if (dtxFallbackToGam) {
          _loadGamAds(config) // Use the internal GAM loader
        }
      })
  } else {
    _loadGamAds(config) // Use the internal GAM loader
  }
}

/**
 * Configure an ad block.  Setup is either immediate or queued.
 */
export const adBlockConfigure = (config: AdBlockConfiguration) => {
  waitForViewabilityAndLoad(config, loadGamAds)
}

/**
 * Configure Google Publisher Tag for an ad block.
 *
 * @param config the configuration for this ad block
 */
export const adBlockConfigureGPT = (config: AdBlockConfiguration) => {
  const { pubwiseConfig, adUnitID, position, dimensionsOverride } = config
  const w = window as any

  const ad_unit_path = getAdBlockPath(pubwiseConfig, adUnitID, position)
  const dfp_account_id = pubwiseConfig.dfp_account_id
  w.googletag.cmd.push(function () {
    if (config.outpageSlot && !config.outpageSlot.customMethod) {
      const w = window as any
      config.outpageSlot.position = w.googletag.enums.OutOfPageFormat.BOTTOM_ANCHOR
    }
    const outPage = config.outpageSlot && config.outpageSlot.customMethod
    // defineSlot and defineOutOfPageSlot want different stuff:tm:
    const slotArgs =
      config.outpageSlot && !outPage
        ? [`/${dfp_account_id}/${ad_unit_path}`, config.outpageSlot.position]
        : [`/${dfp_account_id}/${ad_unit_path}`, getAdBlockDimensions(adUnitID, dimensionsOverride, position), adUnitID]
    const slotFunc = config.outpageSlot && !outPage ? w.googletag.defineOutOfPageSlot : w.googletag.defineSlot
    const adSlot = slotFunc(...slotArgs).addService(w.googletag.pubads())
    if (isStickyAd(adUnitID)) {
      adSlot.setCollapseEmptyDiv(true, true)
    }

    // Our pubwise integration library exposes a custom function that sets custom targeting values on gpt ad slots.
    // We'll do a check just so we can get a warning if it's not there, but that would be bad.
    // This allows us to set values like AAID and lat/long directly on the GPT slot.
    const setGPTAdTargeting =
      w.mp_globals && w.mp_globals.pubwise_integration && w.mp_globals.pubwise_integration.setGPTAdTargeting
    if (typeof setGPTAdTargeting !== 'function') {
      console.error('Missing globally exposed pubwise integration function for setting custom gpt targeting.')
    } else {
      setGPTAdTargeting(adSlot)
    }

    w.gptadslots[adUnitID] = adSlot
    w.googletag.enableServices()

    if (config.outpageSlot && !outPage) {
      w.googletag.cmd.push(function () {
        w.googletag.display(adSlot, [320, 50])
      })
    } else {
      w.googletag.display(adUnitID)
    }

    // If enabled, start Amazon bidding first and let the timeout between Amazon and Pubwise control things (this was recommended by Stephen from Pubwise).
    if (w.mp_globals.features.amazon_ads && w.apstag) {
      const awsSetup = {
        slots: [
          {
            slotID: adUnitID,
            slotName: `${dfp_account_id}/${ad_unit_path}`,
            sizes: getAdBlockDimensions(adUnitID, dimensionsOverride, position)
          }
        ],
        timeout: 2500
      }

      w.apstag.fetchBids(awsSetup, function () {
        w.googletag.cmd.push(function () {
          w.apstag.setDisplayBids()
        })
      })
    }

    // The visibility check is now handled by `waitForViewabilityAndLoad`.
    // We can now directly request the ad, replicating the logic from the old `pubwiseLazyLoad` function.
    if (typeof w.pubwise != 'undefined' && w.pubwise.enabled === true) {
      w.pbjs.que.push(function () {
        neptuneDebug(`Pubwise available, requesting bids for ${adUnitID}`)
        w.pbjs.requestBids({
          adUnitCodes: [adUnitID],
          bidsBackHandler: function () {
            w.pbjs.setTargetingForGPTAsync([adUnitID])
            w.googletag.pubads().refresh([w.gptadslots[adUnitID]])
          }
        })
      })
    } else {
      // Fallback when pubwise is not available
      const loadAd = function () {
        neptuneDebug('Pubwise unavailable, refreshing add directly through GPT.')
        w.googletag.cmd.push(function () {
          w.googletag.pubads().refresh([w.gptadslots[adUnitID]])
        })
      }
      loadAd()
    }
  })
}

/**
 * Configure Google Publisher Tag for a multiplex ad block.
 *
 * @param pubwiseConfig Selected config (page.header_bidding.campaign.language)
 * @param adUnitID      HTML element ID.
 * @param position      Vertical position on the page starting from zero.
 * @param layout        Block layout, edge vs tile.
 */
export const adBlockConfigureGPTMultiplex = (
  pubwiseConfig: any,
  adUnitID: string,
  position: number,
  layout: string
) => {
  const w = window as any

  const multiplexConfig = pubwiseConfig?.ad_unit_paths?.multiplex
  const ad_unit_path = multiplexConfig[layout]
  const dfp_account_id = pubwiseConfig.dfp_account_id

  neptuneDebug(
    `adBlockConfigureGPTMultiplex adUnitID ${adUnitID}, position: ${position}, layout: ${layout} ad_unit_path: ${ad_unit_path} dfp_account_id: ${dfp_account_id}`
  )

  w.googletag.cmd.push(function () {
    const adSlot = w.googletag
      .defineSlot(`/${dfp_account_id}/${ad_unit_path}`, ['fluid'], `${adUnitID}`)
      .addService(w.googletag.pubads())

    w.googletag.pubads().enableSingleRequest()
    w.googletag.enableServices()
    w.googletag.display(`${adUnitID}`)

    const el = document.querySelector(`#${adUnitID}`)

    w.mp_globals.helpers.lazyload(el, function () {
      w.mp_globals.pubwise_integration.setGPTAdTargeting(adSlot)
      w.googletag.pubads().refresh([adSlot])
    })
  })
}

// Helper functions.
// -----------------

/**
 * Sticky ads use different dimensions than other types.
 *
 * These might work better in platform settings.
 */
export const getAdBlockDimensions = (adUnitID, dimensions, position, isDTX: boolean = false) => {
  if (position === AD_POSITION_FLOATING) {
    const w = window as any
    if (w.googletag) {
      const width = window.innerWidth
      let adSizes = []
      if (width >= 1200) {
        // Extra Large Desktop
        adSizes = [
          [320, 50],
          [300, 50],
          [320, 100],
          [970, 90],
          [728, 90],
          [468, 60]
        ]
      } else if (width >= 992) {
        // Large Desktop
        adSizes = [
          [320, 50],
          [300, 50],
          [320, 100],
          [728, 90],
          [970, 90],
          [468, 60]
        ]
      } else if (width >= 768) {
        // Tablet Landscape
        adSizes = [
          [320, 50],
          [300, 50],
          [320, 100],
          [728, 90],
          [468, 60]
        ]
      } else {
        // Tablet Portrait/Large Mobile
        adSizes = [
          [320, 50],
          [300, 50],
          [320, 100]
        ]
      }
      if (!isDTX) {
        return [...adSizes, 'fluid']
      }
      return adSizes
    }
    return DEFAULT_DIMENSIONS_FLOATING
  }
  let d = DEFAULT_DIMENSIONS

  // Override.
  if (dimensions.length) {
    d = dimensions
  } else if (isStickyAd(adUnitID)) {
    d = DEFAULT_DIMENSIONS_STICKY
  }

  return d
}

export const getAdBlockPath = (pubwiseConfig, adUnitID, position) => {
  if (isStickyAd(adUnitID)) {
    try {
      return pubwiseConfig.ad_unit_paths.kargo.edge
    } catch {
      console.log('getAdBlockPath: kargo sticky unit setup invalid, could not find ad_unit_paths.kargo.edge')
    }
  }

  if (position === AD_POSITION_FLOATING && pubwiseConfig.ad_unit_paths.floating) {
    return pubwiseConfig.ad_unit_paths.floating
  }

  return position == 0 ? pubwiseConfig.ad_unit_paths.top : pubwiseConfig.ad_unit_paths.bottom
}

/**
 * Hard coding the Kargo div id here is kind of nasty but it shouldn't really change (it changed).
 */
const isStickyAd = (adUnitID): boolean => {
  return [KARGO_DIV_ID_PRODUCTION, KARGO_DIV_ID_TEST].includes(adUnitID)
}

export const isLazyLoadEnabled = (position, adUnitID, lazyLoadTop = false): boolean => {
  return (lazyLoadTop || !(parseInt(position) === 0 || isStickyAd(adUnitID))) && position != AD_POSITION_FLOATING
}
